import React from 'react';
import { getPhaseRSC, getEventRSC, completeRsc, getPhase } from 'helpers/rsc'
import { useUrlFriendlyToParameters, useGetURL } from "helpers/urlFriendly";
import { getPhases } from 'data/Querys';
import { useQuery } from "@tanstack/react-query";
import Label, { useLabels } from 'components/general/Label';
import { TYPE_DATA, sortDataByField } from 'helpers/utils';
import { Link } from 'react-router-dom';
import { useGetConfigSports, useGetEventUnits } from 'helpers/commonCodes';
import { SCHEDULE_STATUS, getScheduleStatusAsInteger, isLiveStatus } from 'helpers/status';

/**
 * Return component phases 
 * The Live option will be selected by default in case the event has any unit in live.
 * If there is no live the selected phase will be the one defined by the current unit. 
 * The selection of any phase will filter the ribbon to display the units for that selected phase. 
 * This ribbon is expanded in widescreen and hidden in mobile by default. 
 * @funcSelect : Parent function to call in child component (PhaseItem component)
 * Retur phase selected
 */

export default function PhaseRibbonH2H({ funcSelect, phaseActive, items }) {
    const fLabel = useLabels();
    const params = useUrlFriendlyToParameters()
    const { mirs_competition, mirs_language, mirs_rsc, mirs_discipline } = params;
    const eventUnits = useGetEventUnits(params)
    let _event = getEventRSC(mirs_rsc);
    const dataConfigSport = useGetConfigSports(params);
    const isH2H = dataConfigSport?.isH2H
    const noH2hPhases = dataConfigSport?.noH2hPhases;
    const labelSwimOff = fLabel({ data: "SwimOff" });
    //const labelJumpOff = fLabel({ data: "JumpOff" }) //cuando podamos añadir labels, se descomenta
    const labelJumpOff = 'Jump-Off';
    const isDressage = /EQUODRES.+/.test(_event);
    const isEventing = /EQUOEVEN.+/.test(_event);
    const isJumping = /EQUOJUMP.+/.test(_event);
    const isMpn = /MPN.+/.test(_event);
    const isSkb = /SKB.+/.test(_event);
    const isSal = /SAL.+/.test(_event);
    const isCtrSprint = /CTR.SPRINT-+/.test(_event);
    const isSwa = /SWA.+/.test(_event);
    const isSho = /SHO.+/.test(_event);
    const isArc = mirs_discipline === 'ARC';

    _event = isEventing ? 'EQUOEVEN--------------' : isDressage ? 'EQUODRES--------------' : _event;

    const { data, isError, isLoading } = useQuery(
        {
            queryKey: ['phasesData', mirs_competition, mirs_language, _event],
            queryFn: getPhases,
            refetchInterval: 0,
        });

    //solo para obtener la descripcion en equ eventing jumping
    const { data: dataEquEventing, isError: isError2, isLoading: isLoading2 } = useQuery(
        {
            queryKey: ['phasesDataEventing', mirs_competition, mirs_language, 'EQUOEVENINDV----------'],
            queryFn: getPhases,
            refetchInterval: 0,
            enabled: isEventing
        });

    //solo para obtener la descripcion en equ dressage grand prix special
    const { data: dataDresGrandPrixSpecial, isError: isError3, isLoading: isLoading3 } = useQuery(
        {
            queryKey: ['phasesDataDresgrandPrixSpecial', mirs_competition, mirs_language, 'EQUODRESTEAM----------'],
            queryFn: getPhases,
            refetchInterval: 0,
            enabled: isDressage
        });

    //solo para obtener la descripcion en equ dressage grand prix freeStyle
    const { data: dataDresGrandPrixFreeStyle, isError: isError4, isLoading: isLoading4 } = useQuery(
        {
            queryKey: ['phasesDataGrandPrixFreeStyle', mirs_competition, mirs_language, 'EQUODRESINDV----------'],
            queryFn: getPhases,
            refetchInterval: 0,
            enabled: isDressage
        });

    if (isError || isError2 || isError3 || isError4) { return (<></>) }
    if (isLoading || isLoading2 || isLoading3 || isLoading4) { return (<></>) }
    if (!data) { return (<></>) }

    // remove from the array the UNSCHEDULED or CANCELLED phases
    let dataFiltered = data?.filter(phase => {
        const realUnits = phase?.units?.filter(u => u.scheduled !== 'N' && u?.schedule?.status?.code !== 'UNSCHEDULED' && u?.schedule?.status?.code !== 'CANCELLED')
        return Array.isArray(realUnits) && realUnits.length > 0
    })


    //ARC
    if (isArc) {
        dataFiltered = dataFiltered?.filter(phase => {
            const realUnits = phase?.units?.filter(u => u.scheduled !== 'S')
            return Array.isArray(realUnits) && realUnits.length > 0
        })
    }


    let dataClone = [...dataFiltered]
    let result = [];

    /*--------------------------------*/
    let units;
    // swim-off units 
    dataFiltered.forEach((phase) => {
        units = phase?.units?.filter(item => item?.code.endsWith('RR--') && item?.schedule?.status?.code !== 'UNSCHEDULED' && item?.schedule?.status?.code !== 'CANCELLED');
        if (Array.isArray(units) && units.length > 0) {
            let phaseFilter = data.find(item => item.code === phase.code.substring(0, phase.code.length - 2) + "RR")
            if (!phaseFilter) {
                let h = { ...phase };
                h.order = Number(h.order) - 0.5;
                h.shortDescription = mirs_discipline === 'SWM' ? phase.shortDescription + " " + labelSwimOff : labelJumpOff;
                if (mirs_discipline === 'SWM') { h.code = phase.code + "0000RR--" }
                else {
                    h.code = phase.code.substring(0, phase.code.length - 2) + "RR"
                }
                if (mirs_discipline === 'SWM' || mirs_discipline === 'EQU') { dataClone.push(h); }
            }
        }
    })

    // EQU eventing
    if (isEventing) {
        dataClone.forEach((phase, index) => {
            const isEvenJump = phase?.code === 'EQUOEVEN--------------JUMP'
            if (isEvenJump && Array.isArray(phase?.units)) {
                phase.shortDescription = phase.units[0].shortDescription
            }
        })
        dataClone.push(getEvenJumpIndivFinalPhase(dataEquEventing))
    }
    // EQU dressage
    if (isDressage) {
        dataClone = []
        dataFiltered.forEach((phase) => {
            const isDressQual = phase?.code === 'EQUODRES--------------COMP'
            if (isDressQual) {
                const shortDesc = eventUnits?.eventUnits?.find(e => e.code === "EQUODRES--------------COMP000100--")?.shortDescription
                dataClone.push(getDressQualPhase(phase, mirs_rsc, shortDesc))
            }
        })
        dataClone.push(getDresGrandPrixSpecialPhase(dataDresGrandPrixSpecial))
        dataClone.push(getDresGrandPrixFreeStylePhase(dataDresGrandPrixFreeStyle))
    }


    // Combined 
    if (dataConfigSport?.combinedEvents?.some(event => _event.includes(event))) {
        dataConfigSport.isAthCombinedEvent = true
        dataClone.forEach((childEvent, index) => {
            childEvent.code = completeRsc(childEvent.code)
        })
    }

    // MPN
    if (isMpn) {
        dataClone = []
        dataFiltered.forEach((phase, index) => {
            if (Array.isArray(phase?.units)) {
                dataClone = [...dataClone, ...phase?.units]
            }
        })
        result = dataClone.filter(row => filterMpmUnits(row?.code))
        result = result.map(i => {
            i.order = setOrderMpmUnits(i?.code)
            i.shortDescription = i.description
            return i;
        })
    }
    // SWA
    if (isSwa) {
        dataFiltered.forEach((phase, index) => {
            if (phase?.units) {
                result = [...phase?.units]
            }
        })
        result.map(i => {
            i.order = setOrderSwaUnits(i?.code)
            return i
        })
    }

    if (isSho) {
        Array.isArray(dataClone) && dataClone.forEach((phase) => {
            if (phase?.units) {
                if (/SHOX(SK|AP|AR).+FNL-/.test(phase?.code)) {
                    phase?.units?.forEach(unit => {
                        if (/SHOX(SK|AP|AR).+FNL-000[1|2]00--/.test(unit?.code)) result.push(unit)
                    })
                }
                else result.push(phase)
            }
        })
        result.filter(row => (row?.units && row?.units?.find(u =>
            u?.scheduled && (u?.scheduled !== 'N'))) || (row?.scheduled && (row?.scheduled !== 'N')))?.map(i => {
                i.order = /SHOX(SK|AP|AR).+FNL-/.test(i?.code) ? setOrderShoUnits(i?.code) : i?.order
                return i
            })
    }

    /*-------------------------------*/
    if (!isMpn && !isSwa && !isSho) {
        result = dataClone.filter(row => (row?.units && row.units?.find(u => u?.scheduled && (u?.scheduled !== 'N'))))
    }

    //EQU buttons name fix
    if (isJumping) {
        result = result?.map(item => {
            if (item?.code === "EQUOJUMPTEAM----------FNL-") {
                item.shortDescription = eventUnits?.eventUnits?.find(e => e.code === "EQUOJUMPTEAM----------FNL---------")?.shortDescription
            }
            if (item?.code === "EQUOJUMPINDV----------QUAL") {
                item.shortDescription = eventUnits?.eventUnits?.find(e => e.code === "EQUOJUMPINDV----------QUAL--------")?.shortDescription
            }
            if (item?.code === "EQUOJUMPINDV----------FNL-") {
                item.shortDescription = eventUnits?.eventUnits?.find(e => e.code === "EQUOJUMPTEAM----------FNL---------")?.shortDescription
            }
            return item
        })
    }

    //CTR sprint
    if (isCtrSprint) {
        pushFinal5_8(result);
    }

    result = dataConfigSport?.isAthCombinedEvent ? sortDataByField(result, "order", false, TYPE_DATA.STRING) : sortDataByField(result, "order", false, TYPE_DATA.RIBBON);

    if (isSal) {
        result = result?.map(item => {
            const newCode = item?.units?.sort((a, b) => a?.code > b?.code ? 1 : -1)?.find(item => true)?.code;
            item.code = newCode ? newCode : item.code;
            return item
        })
    }

    if (isSkb) {
        result = result?.map(item => {
            if (/SKB...................FNL-/.test(item?.code)) {
                item.code = item?.units?.find(e => e.code?.includes("FNL-000100--"))?.code
            }
            return item
        })
    }

    let postList = ''
    if (isH2H) {

        postList = <nav className='container-phase' aria-labelledby="NavBarRibbonPhases">
            <span id="NavBarRibbonPhases" className="sr-only"><Label data={'LabelRibbonSelector'} /></span>
            <ul className={`container d-flex flex-column flex-md-row `} >
                <PhaseItemLive key='LiveItem' phaseActive={phaseActive} funcSelect={funcSelect} result={result} />
                {Array.isArray(result) && result.map((item, index) => {
                    if (Array.isArray(noH2hPhases) && noH2hPhases.find(p => (new RegExp(p)).test(item?.code))) {
                        return (
                            <PhaseItemHibrid key={index} data={item} phaseActive={phaseActive} funcSelect={funcSelect} />  // {...{ [resouceName]:item }} its the same like  person={item}                         
                        )
                    } else {
                        return (
                            <PhaseItemH2H key={index} data={item} funcSelect={funcSelect} phaseActive={phaseActive} />  // {...{ [resouceName]:item }} its the same like  person={item}                 
                        )
                    }
                })}
            </ul>
        </nav>

    } else {
        postList =
            <nav className='container-phase' aria-labelledby="NavBarRibbonPhases">
                <span id="NavBarRibbonPhases" className="sr-only"><Label data={'LabelRibbonSelector'} /></span>
                <ul className={`container d-flex flex-column flex-md-row `}>
                    {Array.isArray(result) && result.map((item, index) => (
                        <PhaseItem key={index} data={item} />  // {...{ [resouceName]:item }} its the same like  person={item}                 
                    ))}
                </ul>
            </nav>
    }
    return (
        <>
            {postList}
        </>
    )
}


export function PhaseItemH2H({ data, phaseActive, funcSelect }) {
    const params = useUrlFriendlyToParameters();
    let paramsURL = { ...params }
    const getURL = useGetURL()
    let url = undefined
    let unitsFilter = []
    if (Array.isArray(data?.units)) {
        unitsFilter = data?.units.filter(u => u?.type !== 'NONE' && u.scheduled !== 'N' && u?.schedule?.status?.code !== 'UNSCHEDULED' && u?.schedule?.status?.code !== 'CANCELLED')?.sort((a, b) => new Date(a?.schedule?.startDate) - new Date(b?.schedule?.startDate))
    }

    if (unitsFilter?.length > 0) {
        paramsURL.mirs_rsc = unitsFilter[0]?.code
        paramsURL.mirs_event = getEventRSC(unitsFilter[0]?.code)
        url = getURL(paramsURL, paramsURL?.mirs_path)
    }

    let compChild = undefined
    let active = ''
    if (url) {
        compChild = (
            <CustomLink to={url} position={data.order} bracketCode={'FNL'}>
                <span className="text-center phase-box">{data.shortDescription}</span>
            </CustomLink>
        )
        let phaseUrl = getPhaseRSC(params.mirs_rsc)
        active = phaseActive === 'live' ? '' : phaseUrl === getPhaseRSC(data?.code) ? 'active' : ''

    } else {
        compChild = <span className="text-center phase-box"> {data.shortDescription}</span>
        active = phaseActive === data?.code ? 'active' : ''

    }

    const comp = (
        <li className={`phase-item ${active}`}
            onClick={() => { // this is a child event listener
                // e.stopPropagation()
                funcSelect(data.code)
            }}
            onKeyUp={(e) => { // this is a child event listener                            
                if (e.key === 'Enter') {
                    funcSelect(data.code)
                }
            }}
        >
            {compChild}
        </li >
    )

    if ((data.code.indexOf('TMRY') === -1)) {
        return (comp)
    } else { return null }
}

export function PhaseItemLive({ phaseActive, funcSelect, result }) {
    const params = useUrlFriendlyToParameters();
    const getURL = useGetURL()
    //Copy object in order to change 
    let paramsURL = { ...params }


    let liveUnits = []
    if (Array.isArray(result)) {
        result.forEach(r => {
            if (Array.isArray(r?.units)) {
                r.units.forEach(u => {
                    if (u?.type !== 'NONE' && (getScheduleStatusAsInteger(u?.schedule?.status?.code) === SCHEDULE_STATUS.RUNNING || isLiveStatus(u?.schedule?.result?.status?.code))) {
                        liveUnits.push(u)
                    }
                })
            }
        })
    }
    let url = undefined
    if (Array.isArray(liveUnits) && liveUnits.length > 0) {
        liveUnits = liveUnits?.sort((a, b) => new Date(a?.schedule?.startDate) - new Date(b?.schedule?.startDate));
        paramsURL.mirs_rsc = liveUnits[0]?.code
        paramsURL.mirs_event = getEventRSC(liveUnits[0]?.code)
        url = getURL(paramsURL, paramsURL?.mirs_path)
    }

    let compChild = undefined
    if (url) {
        compChild = (
            <CustomLink to={url} >
                <span className="text-center phase-box"><Label data="LabelLive" /></span>
            </CustomLink>
        )

    } else {
        compChild = <span className="text-center phase-box"><Label data="LabelLive" /> </span>
    }


    const comp = (
        <li className={`phase-item ${(phaseActive === 'live') ? "active" : ""}`}
            onClick={(e) => {
                funcSelect("live")
            }}

            onKeyUp={(e) => { // this is a child event listener                            
                if (e.key === 'Enter') {
                    funcSelect("live")
                }
            }}

        >
            {compChild}
        </li >
    )

    return (
        <>
            {comp}
        </>
    )
}

export function PhaseItem({ data }) {

    let sw = true;
    //get url values 
    const params = useUrlFriendlyToParameters();
    const getURL = useGetURL()
    //Copy object in order to change 
    let paramsURL = { ...params }
    //Phase from URL 
    let phaseUrl = getPhaseRSC(paramsURL.mirs_rsc)
    phaseUrl = /..............................RR--/.test(paramsURL.mirs_rsc) ? phaseUrl?.slice(0, -2) + 'RR' : phaseUrl
    phaseUrl = paramsURL.mirs_discipline === 'SWM' && /..............................RR--/.test(paramsURL.mirs_rsc) ? paramsURL.mirs_rsc : phaseUrl

    //Change rsc from url with new value from data  
    if (Array.isArray(data?.units) && data?.units.length === 1 && data.units[0]?.code) {
        paramsURL.mirs_rsc = completeRsc(data.units[0]?.code)// get the unit when the phase has only one unit
    } else {
        paramsURL.mirs_rsc = completeRsc(data?.code)// getLongPhaseRSC(data.code)
    }
    //path url by default
    let path = 'results'

    //url results
    let url = getURL(paramsURL, path)

    //rsc link fix for EQU jumping
    if (/EQUOJUMP.+/.test(paramsURL.mirs_rsc)) {
        const event = paramsURL.mirs_rsc?.slice(0, 8)
        const indvTeam = paramsURL.mirs_rsc?.slice(8, 12)
        const phase = paramsURL.mirs_rsc?.slice(22, 26)
        const checkJumpOff = paramsURL.mirs_rsc?.includes("FNRR")
        paramsURL.mirs_rsc = event + indvTeam + "----------" + (checkJumpOff ? "FNL-" : phase) + (checkJumpOff ? "0002RR" : "000100") + "--"
        url = getURL(paramsURL, path)
    }

    let code
    if (paramsURL.mirs_discipline === 'SWM') {
        let phase = getPhase(paramsURL.mirs_rsc)
        if (data.code.includes(phase + "0000RR--")) {
            code = paramsURL.mirs_rsc
        } else {
            code = data.code

        }
    } else {
        code = getPhaseRSC(data?.code)
    }


    let active = (phaseUrl === code) ? 'active' : ''
    if (paramsURL.mirs_discipline === 'MPN' || paramsURL.mirs_discipline === 'SAL' || paramsURL.mirs_discipline === 'SWA' || /CTR.SPRINT------------FNL-*/.test(params.mirs_rsc) || /SHOX(SK|AP|AR).+FNL-/.test(params.mirs_rsc)) {
        active = params.mirs_rsc === completeRsc(data.code) ? 'active' : ''
    }

    const comp =
        <li className={`phase-item ${active}`} >
            <CustomLink to={url} position={data?.order} bracketCode={'FNL'}>
                <span className="text-center phase-box">{data?.shortDescription}</span>
            </CustomLink>
        </li >

    if ((data?.code.indexOf('TMRY') === -1) && (sw)) {
        return (
            <>
                {comp}
            </>
        )
    } else { return null }

}


export function PhaseItemHibrid({ data, phaseActive, funcSelect }) {
    let sw = true;
    //get url values 
    const params = useUrlFriendlyToParameters();
    const getURL = useGetURL()
    //Copy object in order to change 
    let paramsURL = { ...params }
    //Change rsc from url with new value from data  
    if (Array.isArray(data?.units) && data?.units.length === 1 && data.units[0]?.code) {
        paramsURL.mirs_rsc = completeRsc(data.units[0]?.code)// get the unit when the phase has only one unit
    } else {
        paramsURL.mirs_rsc = completeRsc(data?.code)// getLongPhaseRSC(data.code)
    }
    //path url by default
    let path = 'results'

    //url results
    const url = getURL(paramsURL, path)

    const active = phaseActive === data.code ? 'active' : ''

    const comp =
        <li className={`phase-item ${active}`} onClick={(e) => { funcSelect(data.code) }}>
            <CustomLink to={url} position={data.order} bracketCode={'FNL'}>
                <span className="text-center phase-box">{data.shortDescription}</span>
            </CustomLink>
        </li >

    if ((data.code.indexOf('TMRY') === -1) && (sw)) {
        return (
            <>
                {comp}
            </>
        )
    } else { return null }

}

function CustomLink({ children, to, position, bracketCode }) {
    return (
        <Link to={to} state={{ bracketPosition: position, bracketCode: bracketCode }} >
            {children}
        </Link>

    );
}

function getEvenJumpIndivFinalPhase(dataEquEventing) {
    let dataCopy = [...dataEquEventing]
    const jumpIndiv = dataCopy?.find(phase => phase?.code === 'EQUOEVENINDV----------JUMP' || phase?.code === 'EQUOEVENINDV----------JUMP000100--');
    if (jumpIndiv) {
        const filteredUnits = jumpIndiv?.units?.filter(unit => unit?.code === 'EQUOEVENINDV----------JUMP000100--');
        jumpIndiv.order = Number(jumpIndiv.order) - 0.5;
        jumpIndiv.shortDescription = filteredUnits[0]?.shortDescription
        jumpIndiv.code = filteredUnits[0]?.code
        jumpIndiv.units = filteredUnits
        return jumpIndiv
    }
}

function getDressQualPhase(phase, mirsRsc, shortDesc) {
    let phaseCopy = { ...phase }
    let filteredUnits = phaseCopy?.units?.filter(unit => unit?.code === mirsRsc);
    filteredUnits = filteredUnits?.length === 0 ? phaseCopy?.units?.filter(unit => unit?.code === 'EQUODRES--------------COMP0001D2--') : filteredUnits
    phaseCopy.units = filteredUnits
    phaseCopy.order = 3
    phaseCopy.code = Array.isArray(filteredUnits) && filteredUnits[0].code
    phaseCopy.shortDescription = shortDesc
    return phaseCopy;
}

function getDresGrandPrixSpecialPhase(dataDresGrandPrixSpecial) {
    let dataCopy = dataDresGrandPrixSpecial && [...dataDresGrandPrixSpecial];
    const grandPrixSpecial = dataCopy?.find(phase => phase?.code === 'EQUODRESTEAM----------GPSP' || phase?.code === 'EQUODRESTEAM----------GPSP000100--');
    if (grandPrixSpecial) {
        const filteredUnits = grandPrixSpecial?.units?.filter(unit => unit?.code === 'EQUODRESTEAM----------GPSP000100--');
        //filteredUnits.order = Number(jumpIndiv.order) - 0.5;
        grandPrixSpecial.shortDescription = filteredUnits[0]?.shortDescription
        grandPrixSpecial.code = filteredUnits[0]?.code
        grandPrixSpecial.units = filteredUnits
        grandPrixSpecial.order = 2
        return grandPrixSpecial
    }
}

function getDresGrandPrixFreeStylePhase(dataDresGrandPrixFreeStyle) {
    let dataCopy = dataDresGrandPrixFreeStyle && [...dataDresGrandPrixFreeStyle];
    const grandPrixFreeStyle = dataCopy?.find(phase => phase?.code === 'EQUODRESINDV----------GPFR' || phase?.code === 'EQUODRESINDV----------GPFR000100--');
    if (grandPrixFreeStyle) {
        const filteredUnits = grandPrixFreeStyle?.units?.filter(unit => unit?.code === 'EQUODRESINDV----------GPFR000100--');
        grandPrixFreeStyle.shortDescription = filteredUnits[0]?.shortDescription
        grandPrixFreeStyle.code = filteredUnits[0]?.code
        grandPrixFreeStyle.order = 1
        grandPrixFreeStyle.units = filteredUnits
        return grandPrixFreeStyle
    }
}

function pushFinal5_8(results) {
    const resultsCopy = [...results];
    const final = resultsCopy?.find(item => /CTR.SPRINT------------FNL-/.test(item?.code));
    const final_5_8 = final?.units?.find(item => /CTR.SPRINT------------FNL-000300--/.test(item?.code))
    if (final_5_8) {
        final_5_8.order = '1.5';
        results.push(final_5_8)
    }
}

function filterMpmUnits(rsc) {
    /*
     MPN.INDIVID-----------RANK0001FE--
 
     MPN.INDIVID-----------SFNL0001EQ--
     MPN.INDIVID-----------SFNL0001FE--
     MPN.INDIVID-----------SFNL0001SW00
     MPN.INDIVID-----------SFNL0001LR--
 
     MPN.INDIVID-----------SFNL0002EQ--
     MPN.INDIVID-----------SFNL0002FE--
     MPN.INDIVID-----------SFNL0002SW00
     MPN.INDIVID-----------SFNL0002LR--
 
     MPNWINDIVID-----------FNL-0001EQ--
     MPNWINDIVID-----------FNL-0001FE--
     MPNWINDIVID-----------FNL-0001SW00
     MPNWINDIVID-----------FNL-0001LR--    
    */
    const FILTER = /(MPN.INDIVID-----------RANK0001FE--)|(MPN.INDIVID-----------SFNL0001EQ--)|(MPN.INDIVID-----------SFNL0001FE--)|(MPN.INDIVID-----------SFNL0001SW00)|(MPN.INDIVID-----------SFNL0001LR--)|(MPN.INDIVID-----------SFNL0002EQ--)|(MPN.INDIVID-----------SFNL0002FE--)|(MPN.INDIVID-----------SFNL0002SW00)|(MPN.INDIVID-----------SFNL0002LR--)|(MPN.INDIVID-----------FNL-0001EQ--)|(MPN.INDIVID-----------FNL-0001FE--)|(MPN.INDIVID-----------FNL-0001SW00)|(MPN.INDIVID-----------FNL-0001LR--)/
    return FILTER.test(rsc)
}

function setOrderMpmUnits(rsc) {
    const SORTING = [
        { regEx: /MPN.INDIVID-----------RANK0001FE--/, order: 13 },

        { regEx: /MPN.INDIVID-----------SFNL0001EQ--/, order: 12 },
        { regEx: /MPN.INDIVID-----------SFNL0001FE--/, order: 11 },
        { regEx: /MPN.INDIVID-----------SFNL0001SW00/, order: 10 },
        { regEx: /MPN.INDIVID-----------SFNL0001LR--/, order: 9 },

        { regEx: /MPN.INDIVID-----------SFNL0002EQ--/, order: 8 },
        { regEx: /MPN.INDIVID-----------SFNL0002FE--/, order: 7 },
        { regEx: /MPN.INDIVID-----------SFNL0002SW00/, order: 6 },
        { regEx: /MPN.INDIVID-----------SFNL0002LR--/, order: 5 },

        { regEx: /MPN.INDIVID-----------FNL-0001EQ--/, order: 4 },
        { regEx: /MPN.INDIVID-----------FNL-0001FE--/, order: 3 },
        { regEx: /MPN.INDIVID-----------FNL-0001SW00/, order: 2 },
        { regEx: /MPN.INDIVID-----------FNL-0001LR--/, order: 1 },
    ]
    return SORTING.find(i => i.regEx.test(rsc))?.order

}

function setOrderSwaUnits(rsc) {
    const SORTING = [
        { regEx: /SWA...................FNL-000300--/, order: 3 },
        { regEx: /SWA...................FNL-000200--/, order: 2 },
        { regEx: /SWA...................FNL-000100--/, order: 1 },
    ]
    return SORTING.find(i => i.regEx.test(rsc))?.order
}

function setOrderShoUnits(rsc) {
    const SORTING = [
        { regEx: /SHOX(SK|AP|AR).+FNL-000200--/, order: 2 },
        { regEx: /SHOX(SK|AP|AR).+FNL-000100--/, order: 1 },
    ]
    return SORTING.find(i => i.regEx.test(rsc))?.order
}

export function useGetPhasesRibbon() {
    const { mirs_competition, mirs_language, mirs_rsc } = useUrlFriendlyToParameters()
    let _event = getEventRSC(mirs_rsc);
    const isDressage = /EQUODRES.+/.test(_event);
    const isEventing = /EQUOEVEN.+/.test(_event);
    _event = isEventing ? 'EQUOEVEN--------------' : isDressage ? 'EQUODRES--------------' : _event;
    const { data, isError, isLoading } = useQuery(
        {
            queryKey: ['phasesData', mirs_competition, mirs_language, _event],
            queryFn: getPhases,
            refetchInterval: 0,
        });

    if (isError || isLoading) { return '' }
    let numberOfPhases = 0;
    if (data && Array.isArray(data)) {
        numberOfPhases = data?.filter(item => item?.type === '3')?.length
    }
    return (
        {
            phases: data,
            singlePhase: numberOfPhases <= 1 || numberOfPhases === undefined
        }
    )
}
